// Coverage & Roadmap Slice 2 — read hook over get_coverage_overview().
// Works for anon + authenticated (the RPC is granted to both). Pure
// presentational data only — no PII, no writes. Soft-fails to a
// safe empty view (never throws, never fabricates coverage); the
// bucketing is the tested pure mirror from Slice 1.

import { useCallback, useEffect, useMemo, useState } from "react";
import { getBrowserClient } from "../supabase/browser.ts";
import {
  bucketCoverage,
  describeAccess,
  sportAccessMatrix,
  type CoverageOverview,
  type CoverageBuckets,
  type SportAccessMatrix,
} from "./coverageOverview.ts";

const EMPTY: CoverageOverview = {
  live: [],
  football_activating: [],
  roadmap: [],
  access: { tier: "starter", expansion_quota: 0, football_always: true },
  generated_at: "",
};

export interface UseCoverageOverviewState {
  /** Raw gate-truth payload (for the board view-model). Never
   *  fabricated — soft-fails to the safe EMPTY overview. */
  readonly overview: CoverageOverview;
  readonly buckets: CoverageBuckets;
  readonly access: ReturnType<typeof describeAccess>;
  readonly accessMatrix: SportAccessMatrix;
  readonly tier: string;
  readonly loading: boolean;
  readonly error: string | null;
  readonly refetch: () => Promise<void>;
}

export function useCoverageOverview(): UseCoverageOverviewState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const [data, setData] = useState<CoverageOverview>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const { data: res, error: rpcErr } = await (supabase.rpc as unknown as (
        fn: string,
      ) => Promise<{ data: CoverageOverview | null; error: { message: string } | null }>)(
        "get_coverage_overview",
      );
      if (rpcErr || !res || typeof res !== "object") {
        // Soft-fail: keep the safe empty view (no fabricated rows).
        if (rpcErr) setError(rpcErr.message);
        setData(EMPTY);
      } else {
        setData(res);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setData(EMPTY);
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return useMemo(
    () => ({
      overview: data,
      buckets: bucketCoverage(data),
      access: describeAccess(data.access),
      accessMatrix: sportAccessMatrix(
        data.roadmap,
        data.access.expansion_quota,
      ),
      tier: data.access.tier,
      loading,
      error,
      refetch,
    }),
    [data, loading, error, refetch],
  );
}
